import React, { useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Smartphone, Check, Loader2, ShieldCheck } from "lucide-react";

/**
 * StrexConfirmFlow — to-trinns Strex-betaling:
 * 1. Bruker taster MSISDN → vi sender OTP via /api/wallet/strex/otp
 * 2. Bruker taster OTP fra SMS → vi kaller /api/wallet/strex/confirm
 *
 * Props:
 *   purchaseId — string
 *   amountNok — number (vises i info-tekst)
 *   onSuccess(result) — callback når kreditering er fullført
 *   onFailure(result) — callback når Strex avviser
 */
export default function StrexConfirmFlow({ purchaseId, amountNok, onSuccess, onFailure }) {
  const [step, setStep] = useState("msisdn"); // msisdn | otp
  const [msisdn, setMsisdn] = useState("");
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const [sentTo, setSentTo] = useState("");

  const sendOtp = async () => {
    const clean = msisdn.replace(/\s/g, "");
    if (clean.length < 8) {
      toast.error("Skriv inn et gyldig norsk mobilnummer (8 sifre).");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/wallet/strex/otp", {
        purchase_id: purchaseId,
        msisdn: clean,
      });
      setSentTo(r.data.msisdn);
      setStep("otp");
      toast.success("Engangskode er sendt på SMS.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    if (otp.trim().length < 3) {
      toast.error("Skriv inn engangskoden fra SMS.");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/wallet/strex/confirm", {
        purchase_id: purchaseId,
        otp: otp.trim(),
      });
      if (r.data.ok) {
        onSuccess && onSuccess(r.data);
      } else {
        onFailure && onFailure(r.data);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (step === "msisdn") {
    return (
      <div className="border border-white/10 p-8" data-testid="strex-msisdn-card">
        <div className="flex items-center gap-3 mb-5">
          <Smartphone className="h-6 w-6 text-[var(--magenta)]" />
          <h2 className="font-fraunces italic text-2xl text-[var(--cream)]">Strex – mobilbetaling</h2>
        </div>
        <p className="font-manrope text-sm text-[var(--cream-soft)] mb-5 leading-relaxed">
          Vi sender deg en engangskode på SMS. Når du taster koden, blir{" "}
          <strong className="text-[var(--cream)]">{amountNok} kr</strong> belastet din
          mobilregning. Kun for personer over 18 år.
        </p>
        <label className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)] block mb-1.5">
          MOBILNUMMER
        </label>
        <input
          type="tel"
          inputMode="tel"
          placeholder="f.eks. 90123456"
          value={msisdn}
          onChange={(e) => setMsisdn(e.target.value)}
          className="input-editorial w-full mb-4"
          data-testid="strex-msisdn-input"
        />
        <button
          onClick={sendOtp}
          disabled={busy || !msisdn}
          className="btn-magenta w-full inline-flex items-center justify-center gap-2 py-3 disabled:opacity-50"
          data-testid="strex-send-otp-btn"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Smartphone className="h-4 w-4" />}
          {busy ? "Sender SMS…" : "Send engangskode"}
        </button>
        <p className="font-manrope text-[11px] text-[var(--cream-faint)] mt-4 leading-relaxed">
          <ShieldCheck className="h-3 w-3 inline mr-1" />
          Strex bekrefter at du er over 18 år. Ingen kontaktinfo deles med Norsk Erotikk.
        </p>
      </div>
    );
  }

  // step === "otp"
  return (
    <div className="border border-white/10 p-8" data-testid="strex-otp-card">
      <div className="flex items-center gap-3 mb-5">
        <Smartphone className="h-6 w-6 text-[var(--magenta)]" />
        <h2 className="font-fraunces italic text-2xl text-[var(--cream)]">Tast inn engangskoden</h2>
      </div>
      <p className="font-manrope text-sm text-[var(--cream-soft)] mb-5">
        Vi har sendt en SMS til <strong className="text-[var(--cream)]">{sentTo}</strong>.
        Tast inn engangskoden fra meldingen for å fullføre kjøpet.
      </p>
      <label className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)] block mb-1.5">
        ENGANGSKODE
      </label>
      <input
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        placeholder="••••"
        value={otp}
        onChange={(e) => setOtp(e.target.value.replace(/[^0-9]/g, ""))}
        maxLength={8}
        className="input-editorial w-full mb-4 tracking-[0.4em] text-center text-xl"
        data-testid="strex-otp-input"
      />
      <button
        onClick={confirm}
        disabled={busy || otp.length < 3}
        className="btn-magenta w-full inline-flex items-center justify-center gap-2 py-3 disabled:opacity-50"
        data-testid="strex-confirm-btn"
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
        {busy ? "Bekrefter…" : `Bekreft kjøp på ${amountNok} kr`}
      </button>
      <button
        onClick={() => { setStep("msisdn"); setOtp(""); }}
        disabled={busy}
        className="font-meta text-[11px] tracking-[0.15em] uppercase text-[var(--cream-faint)] hover:text-[var(--cream-soft)] mt-4 inline-block"
        data-testid="strex-back-btn"
      >
        ← Endre mobilnummer
      </button>
    </div>
  );
}
